import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ethers } from 'ethers'
import { getReadProvider } from './useProvider'
import { CHALLENGE_FACTORY_ABI } from '../abis/ChallengeFactoryABI'
import { CHALLENGE_ABI } from '../abis/ChallengeABI'

const LOOK_BACK_BLOCKS = 100_000
const BATCH_SIZE = 2000
const WARNING_THRESHOLD = 5

// ── Types ─────────────────────────────────────────────────────────────────────

export interface EscalatedReport {
  reportId: string
  challengeAddress: string
  reporter: string
  target: string
  round: number
  reason: string
  reasonDetail: string
  status: string
  createdAt: number
  rejectedAt: number | null
  rejectReason: string | null
}

export interface CreatorWarning {
  address: string
  count: number
  lastWarningAt: number | null
  isRevoked: boolean
}

export interface VerifiedApplication {
  address: string
  reason: string
  status: 'pending' | 'rejected'
  createdAt: number
  rejectedAt: number | null
  rejectReason: string | null
}

export interface AdminAuthState {
  jwt: string | null
  isAuthenticating: boolean
  authError: string | null
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export function getAdminAddress(): string {
  return (import.meta.env.VITE_ADMIN_ADDRESS as string | undefined)?.toLowerCase() ?? ''
}

function getApiBase(): string {
  return (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ''
}

function getFactoryAddress(): string {
  return (import.meta.env.VITE_FACTORY_ADDRESS as string | undefined) ?? ''
}

async function adminFetch(url: string, jwt: string, opts: RequestInit = {}): Promise<Response> {
  return fetch(url, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwt}`,
      ...(opts.headers as Record<string, string> | undefined),
    },
  })
}

// ── Admin Auth ────────────────────────────────────���───────────────────────────

export function useAdminLogin(): AdminAuthState & { login: () => Promise<void> } {
  const [jwt, setJwt] = useState<string | null>(null)
  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)

  const login = async () => {
    if (!window.ethereum) {
      setAuthError('MetaMask가 설치되어 있지 않습니다')
      return
    }
    setIsAuthenticating(true)
    setAuthError(null)
    try {
      const provider = new ethers.BrowserProvider(window.ethereum)
      const signer = await provider.getSigner()
      const address = await signer.getAddress()
      const timestamp = Math.floor(Date.now() / 1000)
      const message = `Blockchain Challenge Platform Admin\nAddress: ${address.toLowerCase()}\nTimestamp: ${timestamp}`
      const signature = await signer.signMessage(message)

      const apiBase = getApiBase()
      const res = await fetch(`${apiBase}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address, message, signature }),
      })
      if (!res.ok) {
        const err = (await res.json()) as { error?: string }
        throw new Error(err.error ?? '로그인에 실패했습니다')
      }
      const data = (await res.json()) as { token: string }
      setJwt(data.token)
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : '로그인에 실패했습니다')
    } finally {
      setIsAuthenticating(false)
    }
  }

  return { jwt, isAuthenticating, authError, login }
}

// ── Tab 1: Pending Reports (admin moderation) ─────────────────────────────────

export function usePendingReports(jwt: string | null) {
  const apiBase = getApiBase()
  return useQuery<{ reports: EscalatedReport[] }>({
    queryKey: ['admin', 'pending-reports', jwt],
    queryFn: async () => {
      const res = await adminFetch(`${apiBase}/api/admin/reports?status=pending`, jwt!)
      if (!res.ok) throw new Error('신고 목록을 불러오는 데 실패했습니다')
      return res.json() as Promise<{ reports: EscalatedReport[] }>
    },
    enabled: Boolean(jwt),
    staleTime: 30_000,
  })
}

// Admin upholds a report: invalidate the proof on-chain (eliminates the participant),
// then mark the report handled off-chain.
export function useInvalidateProof(jwt: string | null) {
  const queryClient = useQueryClient()
  const apiBase = getApiBase()
  return useMutation<
    void,
    Error,
    { reportId: string; challengeAddress: string; target: string; round: number }
  >({
    mutationFn: async ({ reportId, challengeAddress, target, round }) => {
      if (!window.ethereum) throw new Error('MetaMask가 설치되어 있지 않습니다')
      const provider = new ethers.BrowserProvider(window.ethereum)
      const signer = await provider.getSigner()
      const contract = new ethers.Contract(challengeAddress, CHALLENGE_ABI, signer)
      const tx = await (
        contract.invalidateProof as (t: string, r: number) => Promise<ethers.TransactionResponse>
      )(target, round)
      await tx.wait()
      // Mark the report resolved (upheld)
      await adminFetch(`${apiBase}/api/admin/reports/${reportId}/resolve`, jwt!, {
        method: 'POST',
        body: JSON.stringify({ status: 'resolved' }),
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'pending-reports'] })
    },
  })
}

export function useAddWarning(jwt: string | null) {
  const queryClient = useQueryClient()
  const apiBase = getApiBase()
  return useMutation<void, Error, string>({
    mutationFn: async (address: string) => {
      const res = await adminFetch(`${apiBase}/api/admin/warnings/${address}`, jwt!, {
        method: 'POST',
      })
      if (!res.ok) throw new Error('경고 부여에 실패했습니다')
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'creator-warnings'] })
    },
  })
}

// Admin rejects a report as frivolous (off-chain status only — no on-chain effect).
export function useRejectReport(jwt: string | null) {
  const queryClient = useQueryClient()
  const apiBase = getApiBase()
  return useMutation<void, Error, string>({
    mutationFn: async (reportId: string) => {
      const res = await adminFetch(`${apiBase}/api/admin/reports/${reportId}/resolve`, jwt!, {
        method: 'POST',
        body: JSON.stringify({ status: 'rejected' }),
      })
      if (!res.ok) throw new Error('신고 처리에 실패했습니다')
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'pending-reports'] })
    },
  })
}

// ── Tab 2: Creator Warnings ────────��──────────────────────────────────────────

export function useCreatorWarnings() {
  const apiBase = getApiBase()
  const factoryAddress = getFactoryAddress()

  return useQuery<CreatorWarning[]>({
    queryKey: ['admin', 'creator-warnings'],
    queryFn: async () => {
      if (!factoryAddress) return []
      const provider = getReadProvider()
      const factory = new ethers.Contract(factoryAddress, CHALLENGE_FACTORY_ABI, provider)

      // Get unique creators from ChallengeCreated events
      const currentBlock = await provider.getBlockNumber()
      const fromBlock = Math.max(0, currentBlock - LOOK_BACK_BLOCKS)
      const creatorSet = new Set<string>()

      for (let from = fromBlock; from <= currentBlock; from += BATCH_SIZE) {
        const to = Math.min(from + BATCH_SIZE - 1, currentBlock)
        const logs = await factory.queryFilter(factory.filters.ChallengeCreated(), from, to)
        for (const log of logs) {
          const creator = (log as ethers.EventLog).args.creator as string
          creatorSet.add(creator.toLowerCase())
        }
      }

      const creators = Array.from(creatorSet)
      const results = await Promise.all(
        creators.map(async (addr): Promise<CreatorWarning> => {
          let count = 0
          let lastWarningAt: number | null = null
          let isRevoked = false

          try {
            const [warningRes, revokedResult] = await Promise.all([
              fetch(`${apiBase}/api/admin/warnings/${addr}`),
              factory.revokedCreators(addr) as Promise<boolean>,
            ])
            if (warningRes.ok) {
              const w = (await warningRes.json()) as { count: number; lastWarningAt: number | null }
              count = w.count
              lastWarningAt = w.lastWarningAt
            }
            isRevoked = revokedResult
          } catch {
            // non-fatal
          }

          return { address: addr, count, lastWarningAt, isRevoked }
        }),
      )

      return results.filter((r) => r.count > 0 || r.isRevoked)
    },
    enabled: Boolean(factoryAddress),
    staleTime: 60_000,
  })
}

export function useRevokeCreator() {
  const queryClient = useQueryClient()
  const factoryAddress = getFactoryAddress()

  return useMutation<void, Error, string>({
    mutationFn: async (creatorAddress: string) => {
      if (!window.ethereum) throw new Error('MetaMask가 설치되어 있지 않습니다')
      const provider = new ethers.BrowserProvider(window.ethereum)
      const signer = await provider.getSigner()
      const factory = new ethers.Contract(factoryAddress, CHALLENGE_FACTORY_ABI, signer)
      const tx = await (
        factory.revokeCreator as (addr: string) => Promise<ethers.TransactionResponse>
      )(creatorAddress)
      await tx.wait()
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'creator-warnings'] })
    },
  })
}

// ── Tab 3: Verified Creator Applications ─────────────────────────────────────

export function useVerifiedApplications(jwt: string | null) {
  const apiBase = getApiBase()
  return useQuery<{ applications: VerifiedApplication[] }>({
    queryKey: ['admin', 'verified-applications', jwt],
    queryFn: async () => {
      const res = await adminFetch(`${apiBase}/api/admin/verified-applications`, jwt!)
      if (!res.ok) throw new Error('자격 신청 목록을 불러오는 데 실패했습니다')
      return res.json() as Promise<{ applications: VerifiedApplication[] }>
    },
    enabled: Boolean(jwt),
    staleTime: 30_000,
  })
}

export function useGrantVerifiedCreator() {
  const queryClient = useQueryClient()
  const factoryAddress = getFactoryAddress()

  return useMutation<void, Error, string>({
    mutationFn: async (creatorAddress: string) => {
      if (!window.ethereum) throw new Error('MetaMask가 설치되어 있지 않습니다')
      const provider = new ethers.BrowserProvider(window.ethereum)
      const signer = await provider.getSigner()
      const factory = new ethers.Contract(factoryAddress, CHALLENGE_FACTORY_ABI, signer)
      const tx = await (
        factory.grantVerifiedCreator as (addr: string) => Promise<ethers.TransactionResponse>
      )(creatorAddress)
      await tx.wait()
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'creator-warnings'] })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'verified-applications'] })
    },
  })
}

export function useRejectApplication(jwt: string | null) {
  const queryClient = useQueryClient()
  const apiBase = getApiBase()

  return useMutation<void, Error, { address: string; rejectReason?: string }>({
    mutationFn: async ({ address, rejectReason }) => {
      const res = await adminFetch(`${apiBase}/api/admin/verified-applications/${address}`, jwt!, {
        method: 'PATCH',
        body: JSON.stringify({ rejectReason: rejectReason ?? null }),
      })
      if (!res.ok) throw new Error('신청 거절에 실패했습니다')
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'verified-applications'] })
    },
  })
}

export { WARNING_THRESHOLD }
